/**
 * SubscriptionPage - Public page showing subscription tiers
 * Users can view plans without logging in, but must log in to subscribe
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { YStack, XStack, H1, H2, Text, Spinner } from 'tamagui';
import { GlassCard, MatrixButton } from '../components/base';

interface SubscriptionPageProps {
  accessToken?: string | null;
}

interface Tier {
  id: string;
  name: string;
  price: number;
  price_display: string;
  interval: string;
  features: string[];
  limits: {
    followed_tickers: number;
    earnings_alerts: boolean;
    email_digests: boolean;
    priority_support: boolean;
  };
  highlighted?: boolean;
}

const API_URL = 'https://nings-api.beamer-labs.workers.dev';

// Default tiers if API fails
const DEFAULT_TIERS: Tier[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    price_display: '$0',
    interval: 'forever',
    features: [
      'Follow up to 3 tickers',
      'Basic earnings summaries',
      'Community access',
    ],
    limits: {
      followed_tickers: 3,
      earnings_alerts: false,
      email_digests: false,
      priority_support: false,
    },
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 9.99,
    price_display: '$9.99',
    interval: 'month',
    features: [
      'Unlimited ticker following',
      'Priority earnings alerts',
      'Email digests',
      'Advanced analytics',
      'Early access to features',
    ],
    limits: {
      followed_tickers: -1, // unlimited
      earnings_alerts: true,
      email_digests: true,
      priority_support: false,
    },
    highlighted: true,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 24.99,
    price_display: '$24.99',
    interval: 'month',
    features: [
      'Everything in Pro',
      'Priority support',
      'Custom alerts',
      'API access',
      'White-glove onboarding',
    ],
    limits: {
      followed_tickers: -1,
      earnings_alerts: true,
      email_digests: true,
      priority_support: true,
    },
  },
];

const TierIcon = ({ tier }: { tier: string }) => {
  switch (tier) {
    case 'free':
      return <Text fontSize="$6" color="$gray10">&#9733;</Text>;
    case 'pro':
      return <Text fontSize="$6" color="$green10">&#9889;</Text>;
    case 'premium':
      return <Text fontSize="$6" color="$yellow10">&#9818;</Text>;
    default:
      return <Text fontSize="$6" color="$gray10">&#9733;</Text>;
  }
};

const SubscriptionPage: React.FC<SubscriptionPageProps> = ({ accessToken }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState<Tier[]>(DEFAULT_TIERS);
  const [loading, setLoading] = useState(true);
  const [currentTier, setCurrentTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    loadTiers();
    if (accessToken) {
      loadCurrentTier();
    }
  }, [accessToken]);

  const loadTiers = async () => {
    try {
      const response = await fetch(`${API_URL}/subscription/tiers`);
      if (response.ok) {
        const data = await response.json();
        if (data.tiers && data.tiers.length > 0) {
          setTiers(data.tiers);
        }
      }
    } catch (err) {
      console.error('Failed to load tiers, using defaults:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentTier = async () => {
    if (!accessToken) return;
    try {
      const response = await fetch(`${API_URL}/subscription/current`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentTier(data.tier || 'free');
      }
    } catch (err) {
      console.error('Failed to load current tier:', err);
    }
  };

  const handleSelectTier = (tier: Tier) => {
    if (!accessToken) {
      // Not logged in - redirect to login with returnTo
      navigate(`/login?returnTo=/subscription?selected=${tier.id}`);
      return;
    }

    if (tier.id === 'free') {
      // Already on free or downgrading - no action needed for now
      return;
    }

    // TODO: Integrate with Stripe checkout
    // For now, show a placeholder
    alert(`Stripe checkout for ${tier.name} tier coming soon!`);
  };

  if (loading) {
    return (
      <YStack flex={1} alignItems="center" justifyContent="center" padding="$4">
        <Spinner size="large" color="$green10" />
        <Text color="$color" marginTop="$4">Loading plans...</Text>
      </YStack>
    );
  }

  return (
    <YStack flex={1} padding="$4" gap="$6" maxWidth={1200} marginHorizontal="auto" width="100%">
      {/* Header */}
      <YStack gap="$2" alignItems="center" paddingVertical="$6">
        <H1 color="$green10" fontFamily="$heading" textAlign="center">
          Choose Your Plan
        </H1>
        <Text color="$gray10" fontSize="$4" textAlign="center" maxWidth={600}>
          Unlock premium features and stay ahead of the market with real-time earnings insights.
        </Text>
        {currentTier && (
          <Text color="$green10" fontSize="$3" marginTop="$2">
            Current plan: {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)}
          </Text>
        )}
      </YStack>

      {/* Tier Cards */}
      <XStack
        flexWrap="wrap"
        justifyContent="center"
        gap="$4"
        paddingBottom="$6"
      >
        {tiers.map((tier) => (
          <GlassCard
            key={tier.id}
            terminal
            width={320}
            minHeight={450}
            padding="$5"
            borderColor={tier.highlighted ? '$green10' : '$borderColor'}
            borderWidth={tier.highlighted ? 2 : 1}
            position="relative"
          >
            {tier.highlighted && (
              <XStack
                position="absolute"
                top={-12}
                left="50%"
                transform={[{ translateX: -50 }]}
                backgroundColor="$green10"
                paddingHorizontal="$3"
                paddingVertical="$1"
                borderRadius="$2"
              >
                <Text color="$background" fontSize="$2" fontWeight="bold">
                  MOST POPULAR
                </Text>
              </XStack>
            )}

            <YStack gap="$4" flex={1}>
              {/* Tier Header */}
              <XStack gap="$2" alignItems="center">
                <TierIcon tier={tier.id} />
                <H2 color="$color" fontFamily="$heading" fontSize="$6">
                  {tier.name}
                </H2>
              </XStack>

              {/* Price */}
              <XStack alignItems="baseline" gap="$1">
                <Text color="$green10" fontSize="$9" fontFamily="$heading" fontWeight="bold">
                  {tier.price_display}
                </Text>
                {tier.interval !== 'forever' && (
                  <Text color="$gray10" fontSize="$3">
                    /{tier.interval}
                  </Text>
                )}
              </XStack>

              {/* Features */}
              <YStack gap="$2" flex={1}>
                {tier.features.map((feature, index) => (
                  <XStack key={index} gap="$2" alignItems="center">
                    <Text color="$green10" fontSize="$3">&#10003;</Text>
                    <Text color="$color" fontSize="$3">
                      {feature}
                    </Text>
                  </XStack>
                ))}
              </YStack>

              {/* CTA Button */}
              <MatrixButton
                variant={tier.highlighted ? 'primary' : 'secondary'}
                onPress={() => handleSelectTier(tier)}
                disabled={currentTier === tier.id}
                marginTop="$4"
              >
                {currentTier === tier.id
                  ? 'Current Plan'
                  : tier.id === 'free'
                  ? 'Get Started'
                  : 'Upgrade'}
              </MatrixButton>
            </YStack>
          </GlassCard>
        ))}
      </XStack>

      {/* FAQ or Additional Info */}
      <YStack alignItems="center" paddingVertical="$4" gap="$2">
        <Text color="$gray10" fontSize="$3" textAlign="center">
          All plans include a 7-day free trial. Cancel anytime.
        </Text>
        <Text color="$gray10" fontSize="$2" textAlign="center">
          Questions? Contact support@nings.app
        </Text>
      </YStack>
    </YStack>
  );
};

export default SubscriptionPage;
